import React, { createContext } from 'react';

import { Guid } from 'src/application/store/model/guid';

import { HistoryItem } from '../../../store/reducers/currentSequencedFlowPositionReducer';
import { isSection, isSequencedFlowItem, Section, SequencedFlowChildrenTypes, SequencedFlowItem } from '@ael/core-api';
import { List } from 'immutable';

type SequencedContentFlowContextProvided = {
  sequencedFlowItem: SequencedFlowItem;
  currentSection: CurrentSection;
  currentContentItem: SequencedFlowChildrenTypes;
  isLastContentItem: boolean;
  structure: List<HistoryItem>;
};

export type CurrentSection = Section | SequencedFlowItem;

type SequencedContentFlowContextProps = {
  sequencedFlowItem: SequencedFlowItem;
  currentSection: CurrentSection;
  currentContentItem: SequencedFlowChildrenTypes;
  structure: List<HistoryItem>;

  children: ((props: SequencedContentFlowContextProvided) => React.ReactNode) | React.ReactNode;
};

const context = createContext<SequencedContentFlowContextProvided>({
  sequencedFlowItem: undefined as never,
  currentSection: undefined as never,
  currentContentItem: undefined as never,
  isLastContentItem: undefined as never,
  structure: undefined as never,
});

const { Provider, Consumer } = context;

export function getItemsInSequence(currentSection: CurrentSection) {
  return isSequencedFlowItem(currentSection) ? currentSection.contentItems : currentSection.children;
}

export function isCurrentItemLast(currentContentItemId: Guid, currentSection: CurrentSection) {
  const itemsInSequence = getItemsInSequence(currentSection);

  return itemsInSequence.last() === currentContentItemId;
}

export const SequencedContentFlowContextProvider: React.FC<SequencedContentFlowContextProps> = ({
  children,
  sequencedFlowItem,
  currentSection,
  currentContentItem,
  structure,
}) => {
  // Section is never the last content item. If it is empty, it is not displayed.
  // This caused a bug where the linear flow was marked as completed, when there was only one section in the branching scenario.
  const isLastContentItem = isSection(currentContentItem)
    ? false
    : isCurrentItemLast(currentContentItem.id, currentSection);
  const provided = React.useMemo(
    () => ({ sequencedFlowItem, currentSection, currentContentItem, isLastContentItem, structure }),
    [currentContentItem, currentSection, isLastContentItem, sequencedFlowItem, structure]
  );

  return <Provider value={provided}>{typeof children === 'function' ? children(provided) : children}</Provider>;
};

export const SequencedContentFlowContextConsumer = Consumer;

export const useSequencedContentFlowContext = () => React.useContext(context);
